import React, { useRef, useState } from 'react'
import { useContent } from '../contexts/ContentContext'
import { useSupabaseVideoUpload } from '../hooks/useSupabaseVideoUpload'
import { useScrollAnimation } from '../hooks/useScrollAnimation'

const About: React.FC = () => {
  const { content, isLoading } = useContent()
  const { activeVideo: video, isLoading: videoLoading } = useSupabaseVideoUpload()
  const { ref, isVisible } = useScrollAnimation()

  // Video player state
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(false)
  const controlsTimeoutRef = useRef<any>(null)

  // Handle play/pause
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  // Handle video time update
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime)
    }
  }

  // Handle duration loaded
  const handleDurationChange = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration)
    }
  }

  // Handle seek
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value)
    setCurrentTime(newTime)
    if (videoRef.current) {
      videoRef.current.currentTime = newTime
    }
  }

  // Handle volume toggle
  const toggleVolume = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
      setIsMuted(!isMuted)
    }
  }

  // Handle fullscreen
  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (!isFullscreen) {
        if (videoRef.current.requestFullscreen) {
          videoRef.current.requestFullscreen()
        }
      } else {
        if (document.fullscreenElement) {
          document.exitFullscreen()
        }
      }
      setIsFullscreen(!isFullscreen)
    }
  }

  // Show controls and auto-hide
  const handleMouseMove = () => {
    setShowControls(true)
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false)
      }
    }, 3000)
  }

  // Format time display
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }

  if (isLoading) {
    return (
      <section id="nosotros" className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando información...</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="nosotros" className="section-padding bg-gray-50" ref={ref}>
      <div className="container-max">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className={`${isVisible ? 'animate-slide-in-left' : ''}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              {content.about?.title || 'Sobre Jenny Kids'}
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              {content.about?.description || 'Somos una marca especializada en ropa infantil que combina moda, comodidad y calidad. Nuestra misión es vestir a los más pequeños con prendas que les permitan expresar su personalidad mientras se sienten cómodos y seguros.'}
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Con más de {content.about?.stats?.years || 10} años de experiencia en el mercado, hemos desarrollado colecciones
              únicas que reflejan las últimas tendencias en moda infantil, siempre priorizando
              la calidad de los materiales y la comodidad de los niños.
            </p>

            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-verano-primary mb-2">{content.about?.stats?.years || 10}+</div>
                <div className="text-gray-600">Años de experiencia</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-verano-primary mb-2">{content.about?.stats?.families || 1000}+</div>
                <div className="text-gray-600">Familias felices</div>
              </div>
            </div>

            <button className="bg-black text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-800 transition-colors duration-200">
              Conoce más sobre nosotros
            </button>
          </div>

          <div className={`relative ${isVisible ? 'animate-slide-in-right' : ''}`}>
            {/* Video de la tienda o placeholder */}
            <div
              className="bg-gradient-to-br from-verano-primary to-verano-secondary rounded-2xl overflow-hidden h-96 relative group"
              onMouseMove={handleMouseMove}
              onMouseLeave={() => isPlaying && setShowControls(false)}
            >
              {video && !videoLoading ? (
                <div className="relative w-full h-full">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={video.video_url}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleDurationChange}
                    onEnded={() => setIsPlaying(false)}
                  >
                    <source src={video.video_url} type="video/mp4" />
                    Tu navegador no soporta el elemento de video.
                  </video>

                  {/* Botón de sonido — siempre visible, esquina superior derecha */}
                  <button
                    onClick={toggleVolume}
                    className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-black bg-opacity-50 hover:bg-opacity-70 text-white text-xs px-3 py-1.5 rounded-full transition-all duration-200 backdrop-blur-sm"
                    title={isMuted ? 'Activar sonido' : 'Silenciar'}
                  >
                    <span className="text-base leading-none">{isMuted ? '🔇' : '🔊'}</span>
                    <span className="font-medium">{isMuted ? 'Activar sonido' : 'Silenciar'}</span>
                  </button>

                  {/* Overlay con texto */}
                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-end justify-center pb-8 pointer-events-none">
                    <div className="text-center text-white">
                      <h3 className="text-2xl font-bold mb-2 drop-shadow-lg">Nuestra Tienda</h3>
                      <p className="text-lg opacity-90 drop-shadow-md">
                        Visítanos y descubre nuestro mundo de moda infantil
                      </p>
                    </div>
                  </div>

                  {/* Custom Video Controls - Appear on hover */}
                  <div
                    className={`absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black via-transparent to-transparent p-4 transition-opacity duration-300 ${
                      showControls ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {/* Timeline/Progress Bar */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-white text-xs font-medium whitespace-nowrap">
                        {formatTime(currentTime)}
                      </span>
                      <input
                        type="range"
                        min="0"
                        max={duration || 0}
                        value={currentTime}
                        onChange={handleSeek}
                        className="flex-1 h-1 bg-gray-600 rounded cursor-pointer accent-verano-primary"
                      />
                      <span className="text-white text-xs font-medium whitespace-nowrap">
                        {formatTime(duration)}
                      </span>
                    </div>

                    {/* Control Buttons */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        {/* Play/Pause Button */}
                        <button
                          onClick={togglePlay}
                          className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-40 rounded-full transition-all duration-200 text-white text-sm"
                          title={isPlaying ? 'Pausar' : 'Reproducir'}
                        >
                          {isPlaying ? '⏸' : '▶'}
                        </button>

                        {/* Volume Button */}
                        <button
                          onClick={toggleVolume}
                          className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-40 rounded-full transition-all duration-200 text-white text-sm"
                          title={isMuted ? 'Desmuteado' : 'Mute'}
                        >
                          {isMuted ? '🔇' : '🔊'}
                        </button>
                      </div>

                      {/* Fullscreen Button */}
                      <button
                        onClick={toggleFullscreen}
                        className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-40 rounded-full transition-all duration-200 text-white text-sm"
                        title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                      >
                        {isFullscreen ? '⛶' : '⛶'}
                      </button>
                    </div>
                  </div>

                  {/* Play Button Overlay (Center) - Shows when paused */}
                  {!isPlaying && (
                    <button
                      onClick={togglePlay}
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-50 transition-all duration-200 group hover:scale-110"
                      title="Reproducir"
                    >
                      <div className="text-6xl text-white drop-shadow-lg">▶</div>
                    </button>
                  )}
                </div>
              ) : videoLoading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-lg">Cargando video...</p>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-white">
                    <span className="text-8xl mb-4 block">🏪</span>
                    <h3 className="text-2xl font-bold mb-2">Nuestra Tienda</h3>
                    <p className="text-lg opacity-90">
                      Visítanos y descubre nuestro mundo de moda infantil
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Mapa de Google Maps */}
            <div className="mt-6 bg-white rounded-lg p-4 shadow-lg">
              <div className="h-48 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1988.2698993152264!2d-74.14256182883496!3d4.675805696956262!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8e3f9c8c33e0b8ff%3A0xa1275c730779d84!2sKascanuez!5e0!3m2!1ses!2sco!4v1759602532311!5m2!1ses!2sco"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación de Jenny Kids - Centro Comercial Kascanues"
                ></iframe>
              </div>
              <div className="mt-3 text-center">
                <p className="text-sm text-gray-600 font-medium">
                  Centro Comercial Kascanues Locales 137-138
                </p>
                <p className="text-xs text-gray-500">
                  Fontibón, Bogotá - Cra. 100 #20-54
                </p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=4.675805696956262,-74.14256182883496"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Ver en Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default About
